import { eventNames, useCoreEvents } from '@carvana/core-events';
import DispatcherEvent from '@carvana/merch-native/DispatcherEvent';
import { usePromise } from '@carvana/merch-react-hooks';
import isEmpty from 'ramda/src/isEmpty';
import { useContext, useState } from 'react';
import { ToastActions } from '~/components/common/FavoritesToastContent/types';
import useDisplayFavoritesToast from '~/components/common/FavoritesToastContent/useDisplayFavoritesToast';
import {
  useUserEmail,
  useUserId,
  useUserLocation
} from '~/context/common/UserContext';
import AppliedFiltersContext from '~/context/srp/AppliedFiltersContext';
import SavedSearchesContext from '~/context/srp/SavedSearchesContext';
import { SavedSearch } from '~/contracts/srp';
import useIsInFavoritesAndSavedSearchExperiment from '~/hooks/common/useIsInFavoritesAndSavedSearchExperiment';
import useLogInAndSaveSearch from '~/hooks/srp/useLogInAndSaveSearch';
import HeartIcon from '~/icons/srp/HeartIcon';
import sendMessageToNative from '~/native/srp/dispatchers/sendMessageToNative';
import { deleteSavedSearchRCB } from '~/services/common/savedSearch';
import { BrowserCookies } from '~/utilities';
import { generateErrorLogger } from '~/utilities/loggers';
import { useCurrentSavedSearchId } from './useSavedSearchApplied';

const SaveSearchButton = () => {
  const mounted = usePromise();
  const { emit } = useCoreEvents();
  const { savedSearches } = useContext(SavedSearchesContext);
  const { appliedFilters } = useContext(AppliedFiltersContext);
  const displayFavoritesToast = useDisplayFavoritesToast();
  const browserCookieId = BrowserCookies.get('BrowserCookieId') ?? '';
  const userId = useUserId();
  const email = useUserEmail();
  const { zip } = useUserLocation();
  const saveSearch = useLogInAndSaveSearch();
  const savedSearchId = useCurrentSavedSearchId();
  const [isDeleting, setIsDeleting] = useState(false);
  const savedSearchIsApplied = savedSearchId !== null;
  const isInFavoritesAndSavedSearchExperiment =
    useIsInFavoritesAndSavedSearchExperiment();
  const savedSearchIsDisabled =
    isEmpty(appliedFilters) ||
    isDeleting ||
    (savedSearchIsApplied && !isInFavoritesAndSavedSearchExperiment);

  const deleteSearch = () => {
    if (!savedSearchId) return;

    setIsDeleting(true);

    mounted(
      deleteSavedSearchRCB({
        browserCookieId,
        email,
        savedSearchId,
        userId,
        zip5: zip
      })
    )
      .then(res => {
        const dataForContext: SavedSearch[] = res.data.map(search => ({
          name: search.name,
          savedSearchId: search.savedSearchId,
          url: search.cvnaId
        }));
        savedSearches.setData(dataForContext ?? []);
        sendMessageToNative(DispatcherEvent.SavedSearchSaved, {});
        emit(eventNames.favorites.mfe.refresh, {
          searches: res.data,
          source: 'app'
        });
        displayFavoritesToast(ToastActions.remove);
      })
      .catch(generateErrorLogger('Delete saved search'))
      .finally(() => setIsDeleting(false));
  };

  const handleClick = () => {
    if (savedSearchIsApplied) {
      deleteSearch();
    } else {
      saveSearch();
    }
  };

  return (
    <button
      type="button"
      className="relative border-none bg-transparent p-0 text-blue-6 disabled:text-grey-3"
      data-qa="Save-Search"
      data-analytics-id="Save Search"
      data-analytics-attributes={JSON.stringify({
        description: 'save search button',
        action: savedSearchIsApplied ? 'remove' : 'add'
      })}
      onClick={handleClick}
      disabled={savedSearchIsDisabled}
    >
      <HeartIcon
        className="relative top-[3px] mr-[8px]"
        isSavedSearch={savedSearchIsApplied && !isDeleting}
      />
      <span className="tl-header-xs m:tl-header-s">
        {savedSearchIsApplied ? 'Saved' : 'Save Search'}
      </span>
    </button>
  );
};

export default SaveSearchButton;
